import api from "@/services/api.service";
import { ActionTree } from "vuex";
import {
	UserManagementState,
	UserDetails,
	UserGroup,
	ContactRole,
	UserRole,
	Permission,
	Contact,
	ContactUser,
	ResetPasswordDetails,
	UserQuestionsUpdate	,
	RenewPasswordDetails,
	PasswordChangeResult,
	DomainGroup,
	Domain
} from "./types";
import { RootState } from "@/store/types";
import { GroupWithPermissions } from "../areas/types";
import domainsApi from "@/services/api.domains.service";

export const actions: ActionTree<UserManagementState, RootState> = {
	async getUserById({ commit }, userId: number) {
		return await api.getUserById(userId);
	},

	async updateUser({ commit }, updatedUser: UserDetails) {
		commit("updateContact", updatedUser);
		return await api.updateUser(updatedUser);
	},

	async updateRole({ commit, rootState }, params) {
		const res = await api.updateRole({
			updatedUser: params.updatedUser,
			roleIds: params.roleIds,
			groupIds: params.groups
		});
		commit("updateContactUser", params);

		commit("setGroupRole", {
			groups: params.groups,
			userId: params.updatedUser.userID
		});

		return res;
	},

	async deleteUser({ commit }, userId: number): Promise<void> {
		const res = await api.deleteUser(userId);
		commit("removeUser", userId);
		return res;
	},

	async createUser({ commit }, newUser: UserDetails) {
		const { data }: { data: number } = await api.createUser(newUser);
		newUser.userID = data;
		commit("setUser", newUser);

		const contact: ContactUser = {
			address: newUser.address,
			contactRoleGroups: [],
			contactRoles: null,
			email: newUser.email,
			groupID: newUser.groupID,
			isSiteStaff: newUser.isSiteStaff,
			name: newUser.name,
			permissions: newUser.permissions,
			role: null,
			sitePassword: null,
			telephone: newUser.telephone,
			userID: newUser.userID,
			username: newUser.username,
			notes: newUser.notes,
			mobile: newUser.mobile,
			receiveIncidentEmails: newUser.receiveIncidentEmails,
			receiveUpdateEmails: newUser.receiveUpdateEmails
		};
		commit("setContact", contact);
		return data;
	},

	async getUserRoles({ commit }, groupId: number) {
		const userRoles = (await api.getUserRoles(groupId)) as UserRole[];
		commit("setUserRoles", userRoles);
		return userRoles;
	},

	async loadPermissions({ commit }) {
		commit("setPermissions", await api.permissionsGet());
	},

	async loadUserDomains({ commit }) {
		commit("setUserDomains", await api.domainsGet());
	},

	async loadAllDomains({ commit }): Promise<void> {
		commit("setDomains", await domainsApi.getAllDomains());
	},

	async createDomain({ commit }, domain: Domain):Promise<void> {
		commit("setDomain", await domainsApi.createDomain(domain));
	},

	async updateDomain({ commit }, domain: Domain): Promise<void> {
		commit("updateDomain", await domainsApi.updateDomain(domain));
	},

	async deleteDomain({ commit }, domainId: number): Promise<void> {
		await domainsApi.deleteDomain(domainId)
		commit("removeDomain", domainId);
	},

	async loadDomainGroups({ commit }): Promise<void> {
		commit("setDomainGroups", await api.domainGroupsGet());
	},

	async createDomainGroup({ commit }, domainGroup: DomainGroup): Promise<void> {
		commit("setDomainGroup", await api.domainGroupsPost(domainGroup));
	},

	async deleteDomainGroup({ commit }, domainGroup: DomainGroup): Promise<void> {
		commit("removeDomainGroup", await api.domainGroupsDelete(domainGroup));
	},

	async updateDomainGroup({ commit }, { domainId, userGroupId, domainGroup }: { domainId: number; userGroupId: number; domainGroup: DomainGroup; }): Promise<void> {
		await api.domainGroupUpdate(domainId, userGroupId, domainGroup);
		commit("updateDomainGroup", { domainId, userGroupId, domainGroup });
	},

	async getUserGroup({ commit }) {
		return (await api.getUserGroup()) as UserGroup;
	},

	async loadUserGroups({ commit }) {
		const userGroups = (await api.getUserGroup()) as UserGroup[];
		commit("setUserGroups", userGroups);
	},

	async getContactRole({ commit }, userId) {
		return (await api.getContactRole(userId)) as ContactRole[];
	},

	async getContactRoleGroups({ commit }, userId) {
		return (await api.getContactRoleGroups(userId)) as ContactRole[];
	},

	async loadContactRoleList({ commit }) {
		const { data }: { data: ContactRole[] } = await api.loadContactRoleList();
		commit("setContactRoles", data);
		return data;
	},

	async updateContactRole({ commit }, updatedContactRole: ContactRole) {
		return await api.updateContactRole(updatedContactRole);
	},

	async deleteContactRole({ commit }, deleteContactRole: ContactRole) {
		return await api.deleteContactRole(deleteContactRole);
	},

	async createContactRole({ commit }, newContactRole: ContactRole) {
		return await api.createContactRole(newContactRole);
	},

	async loadContactsList({ commit }, regionUrl?: string) {
		const { data }: { data: ContactUser[] } = await api.loadContactsList(regionUrl);
		commit("setContacts", data);
		return data;
	},

	async getContacts({ commit }, { page, pageSize, filter }: { page: number; pageSize: number; filter: string }) {
		const { data }: { data: ContactUser[] } = await api.getContacts(page, pageSize, filter);
		commit("setContacts", data);
	},

	async removeContact({ commit }, userId: number) {
		await api.deleteUser(userId);
		commit("removeContact", userId);
	},

	async getRecoveryQuestions({ commit }) {
		return await api.getRecoveryQuestions();
	},

	async getUserQuestions({ commit }, username: string) {
		return await api.getUserQuestions(username);
	},

	async resetUserPassword({ commit }, resetDetails: ResetPasswordDetails) {
		return (await api.resetPassword(resetDetails)).data;
	},

	async updateUserQuestions({ commit }, updateUserQuestions: UserQuestionsUpdate): Promise<number> {
		return (await api.setUserQuestions(updateUserQuestions)).data;
	},

	async renewUserPassword({ commit }, renewDetails: RenewPasswordDetails): Promise<PasswordChangeResult> {
		return (await api.renewPassword(renewDetails)).data;
	},
};
